import { Link, Text, View } from '@react-pdf/renderer'
import type React from 'react'
import type { MapFiltersType } from '~/atoms'
import { tw } from '~/components/pdfs/tw'
import {
  getFilteredLeaseSpaces,
  getNormalizedRate,
  type Listing,
  type ListingLeaseSpace,
  type ListingTransaction,
} from '~/models'
import { cn } from '~/utils'

const SALE_PRICE_LIMITS: Record<string, { min: number; max: number }> = {
  Industrial: { min: 50000, max: 50_000_000 },
  Office: { min: 100000, max: 100000000 },
  Retail: { min: 75000, max: 75000000 },
  Land: { min: 10000, max: 20000000 },
  Hospitality: { min: 200000, max: 200000000 },
  Multifamily: { min: 100000, max: 500000000 },
  Other: { min: 10000, max: 100000000 },
}

const LEASE_RATE_LIMITS: Record<string, { min: number; max: number }> = {
  Industrial: { min: 2, max: 300 },
  Office: { min: 2, max: 300 },
  Retail: { min: 2, max: 300 },
  Land: { min: 0.3, max: 300 }, // Different calculation for land
  Hospitality: { min: 2, max: 300 }, // Use general bounds
  Multifamily: { min: 2, max: 300 }, // Use general bounds
  Other: { min: 2, max: 300 }, // Fallback for unknown types
}

function isValidSalePrice(price: number, propertyTypes: string[]): boolean {
  const propertyType = propertyTypes[0]
  const limits =
    propertyType in SALE_PRICE_LIMITS
      ? SALE_PRICE_LIMITS[propertyType]!
      : SALE_PRICE_LIMITS['Other']
  return price >= limits.min && price <= limits.max
}

function isValidLeaseRate(rate: number, propertyTypes: string[]): boolean {
  const propertyType = propertyTypes[0]

  const limits =
    propertyType in LEASE_RATE_LIMITS
      ? LEASE_RATE_LIMITS[propertyType]!
      : LEASE_RATE_LIMITS['Other']
  return rate >= limits.min && rate <= limits.max
}

// Sale Listing Comp utilities
function getNormalizedSalePrice(listing: Listing): number | undefined {
  let price = listing.meta.price
  if (!price) return undefined

  if (listing.meta.priceType === 'sf') {
    if (listing.meta.buildingSf) {
      price = Math.round(price * listing.meta.buildingSf)
    } else {
      return undefined
    }
  } else if (listing.meta.priceType === 'acre') {
    if (listing.meta.acres) {
      price = Math.round(price * listing.meta.acres)
    } else {
      return undefined
    }
  }
  return price
}

// Copied from get filter values. Need to create helpers for each piece
export function getSalePrice(listing: Listing) {
  let price = listing.meta.price || undefined
  let priceMax = listing.meta.priceMax || listing.meta.price || undefined
  if (price && priceMax) {
    // Building sf or acres should be defined per zod validation
    if (listing.meta.priceType === 'sf') {
      if (listing.meta.buildingSf) {
        price = Math.round(price * listing.meta.buildingSf)
        priceMax = Math.round(priceMax * listing.meta.buildingSf)
      } else {
        price = undefined
        priceMax = undefined
      }
    } else if (listing.meta.priceType === 'acre') {
      if (listing.meta.acres) {
        price = Math.round(price * listing.meta.acres)
        priceMax = Math.round(
          priceMax * (listing.meta.acresMax || listing.meta.acres)
        )
      } else {
        price = undefined
        priceMax = undefined
      }
    }
  }
  return priceMax || price
}

export type SoldTransactionRow = {
  _id: string
  address: string | React.ReactNode
  date: string
  confidential?: boolean
  propertyType: string
  soldPrice?: number
  listedPrice?: number
  buildingSf?: number
  acres?: number
  lotSizeSf?: number
  soldPricePerAcre?: number
  soldPricePerSf?: number
  listedPricePerSf?: number
}

export function getSoldTransactionsData(
  transactions: (ListingTransaction & { listing: Listing })[],
  isLandReport: boolean,
  includeConfidential?: boolean
) {
  const data = transactions.map((transaction, index) => {
    const soldPrice =
      !transaction.confidential || includeConfidential
        ? transaction.sale?.price
        : undefined
    const listedPrice = getSalePrice(transaction.listing)
    const buildingSf =
      transaction.sale?.buildingSf || transaction.listing.meta.buildingSf
    const acres = transaction.sale?.acres || transaction.listing.meta.acres

    const row: SoldTransactionRow = {
      _id: transaction._id,
      address: (
        <>
          <Link
            style={tw('text-black')}
            href={`https://${transaction.listing.organization.slug}.resimplifi.com/listings/${transaction.listing.slug}`}>
            {`${index + 1}. ${transaction.listing.property.address || transaction.listing.name}`}
          </Link>
          {transaction.confidential && (
            <View
              style={tw(
                cn(
                  'ml-2 mt-1 rounded px-1 py-0.5 text-[8px]',
                  index % 2 === 0 ? 'bg-gray-100' : 'bg-white'
                )
              )}>
              <Text>Confidential</Text>
            </View>
          )}
        </>
      ),
      date: transaction.date.split('T')[0],
      confidential: !!transaction.confidential,
      propertyType: transaction.propertyType || '',
      soldPrice,
      listedPrice,
      buildingSf,
      acres,
    }

    if (isLandReport && acres) {
      row.lotSizeSf = Math.round(acres * 43560)
      if (soldPrice) {
        row.soldPricePerAcre = Math.round(soldPrice / acres)
        row.soldPricePerSf =
          Math.round((soldPrice / (acres * 43560)) * 100) / 100
      }
      if (listedPrice) {
        // row.listedPricePerAcre = `$${Math.round(listedPrice / acres).toLocaleString()}/ac`
        row.listedPricePerSf =
          Math.round((listedPrice / (acres * 43560)) * 100) / 100
      }
    } else {
      if (soldPrice && buildingSf) {
        row.soldPricePerSf = Math.round((soldPrice / buildingSf) * 100) / 100
      }
      if (listedPrice && buildingSf) {
        row.listedPricePerSf =
          Math.round((listedPrice / buildingSf) * 100) / 100
      }
    }

    return row
  })
  return data
}

export type CompsStatsData = {
  averageSoldPrice: number
  averageListedPrice: number
  averageSoldPricePerSf: number
  averageSoldPricePerAcre: number
  averageListedPricePerSf: number
  averageBuildingSf: number
  averageAcres: number
  averageAcresSf: number
}

export function getCompsStatsData(
  transactions: (ListingTransaction & { listing: Listing })[],
  isLandReport: boolean
): CompsStatsData {
  const soldPricesList: number[] = []
  const listedPricesList: number[] = []
  const buildingSfList: number[] = []
  const acresList: number[] = []
  const lotSizeSf: number[] = []
  const soldPricePerSfList: number[] = []
  const soldPricePerAcreList: number[] = []
  const listedPricePerSfList: number[] = []

  getSoldTransactionsData(transactions, isLandReport, true).forEach((data) => {
    if (data.soldPrice) soldPricesList.push(data.soldPrice)
    if (data.listedPrice) listedPricesList.push(data.listedPrice)
    if (data.buildingSf) buildingSfList.push(data.buildingSf)
    if (data.acres) acresList.push(data.acres)
    if (data.lotSizeSf) lotSizeSf.push(data.lotSizeSf)
    if (data.soldPricePerSf) soldPricePerSfList.push(data.soldPricePerSf)
    if (data.soldPricePerAcre) soldPricePerAcreList.push(data.soldPricePerAcre)
    if (data.listedPricePerSf) listedPricePerSfList.push(data.listedPricePerSf)
  })

  const averageSoldPrice =
    soldPricesList.length > 0
      ? soldPricesList.reduce((a, b) => a + b) / soldPricesList.length
      : 0
  const averageListedPrice =
    listedPricesList.length > 0
      ? listedPricesList.reduce((a, b) => a + b) / listedPricesList.length
      : 0
  const averageSoldPricePerSf =
    soldPricePerSfList.length > 0
      ? soldPricePerSfList.reduce((a, b) => a + b) / soldPricePerSfList.length
      : 0
  const averageSoldPricePerAcre =
    soldPricePerAcreList.length > 0
      ? soldPricePerAcreList.reduce((a, b) => a + b) /
        soldPricePerAcreList.length
      : 0
  const averageListedPricePerSf =
    listedPricePerSfList.length > 0
      ? listedPricePerSfList.reduce((a, b) => a + b) /
        listedPricePerSfList.length
      : 0
  const averageBuildingSf =
    buildingSfList.length > 0
      ? buildingSfList.reduce((a, b) => a + b) / buildingSfList.length
      : 0
  const averageAcres =
    acresList.length > 0
      ? acresList.reduce((a, b) => a + b) / acresList.length
      : 0
  const averageAcresSf =
    lotSizeSf.length > 0
      ? lotSizeSf.reduce((a, b) => a + b) / lotSizeSf.length
      : 0

  return {
    averageSoldPrice,
    averageListedPrice,
    averageSoldPricePerSf,
    averageSoldPricePerAcre,
    averageListedPricePerSf,
    averageBuildingSf,
    averageAcres,
    averageAcresSf,
  }
}

export type LeaseTransactionRow = {
  _id: string
  address: string | React.ReactNode
  spaceName: string
  leasedRate?: number
  listedRate?: number
  availableSf?: number
  acres?: number
  lotSizeSf?: number
  date: string
  confidential?: boolean
  propertyType: string
}

export function getLeaseTransactionsData(
  transactions: (ListingTransaction & { listing: Listing })[],
  isLandReport: boolean,
  includeConfidential?: boolean
  // includeOnlyValidData?: boolean
) {
  const data = transactions.map((transaction, index) => {
    const lease = transaction.lease
    const leasedRate =
      (!transaction.confidential || includeConfidential) && lease?.rate
        ? getNormalizedRate(
            lease.rate,
            lease.rateType,
            isLandReport ? undefined : lease.footage,
            isLandReport ? lease.acres : undefined
          )
        : undefined
    const leaseSpace = (transaction.listing.leaseSpaces ?? []).find(
      (space) => space._id === lease?.space?._id
    )
    const listedRate = leaseSpace?.minRate
      ? getNormalizedRate(
          leaseSpace.minRate,
          leaseSpace.rateType,
          isLandReport ? undefined : leaseSpace.minAvailableSf,
          isLandReport ? leaseSpace.acres : undefined
        )
      : undefined

    const acres = lease?.acres || leaseSpace?.acres
    const row: LeaseTransactionRow = {
      _id: transaction._id,
      address: (
        <>
          <Link
            style={tw('text-black')}
            href={`https://${transaction.listing.organization.slug}.resimplifi.com/listings/${transaction.listing.slug}`}>
            {`${index + 1}. ${transaction.listing.property.address || transaction.listing.name}`}
          </Link>
          {transaction.confidential && (
            <View
              style={tw(
                cn(
                  'ml-2 mt-1 rounded px-1 py-0.5 text-[8px]',
                  index % 2 === 0 ? 'bg-gray-100' : 'bg-white'
                )
              )}>
              <Text>Confidential</Text>
            </View>
          )}
        </>
      ),
      spaceName: lease?.space?.name || '-',
      leasedRate,
      listedRate,
      availableSf: lease?.footage || leaseSpace?.minAvailableSf,
      acres,
      lotSizeSf: acres ? Math.round(acres * 43560) : undefined,
      date: transaction.date.split('T')[0],
      confidential: !!transaction.confidential,
      propertyType:
        transaction.propertyType || transaction.listing.propertyTypes[0] || '',
    }

    return row
  })
  return data
}

export function getLeaseCompsTableData(
  leasedData: LeaseTransactionRow[],
  isLandReport: boolean
) {
  return leasedData
    .filter((row) => {
      if (isLandReport && !row.acres) return false
      return true
    })
    .map((row) => {
      const listedRateValid = row.listedRate
        ? isValidLeaseRate(row.listedRate, [row.propertyType])
        : false
      const leasedRateValid = row.leasedRate
        ? isValidLeaseRate(row.leasedRate, [row.propertyType])
        : false
      if (!listedRateValid) {
        row.listedRate = undefined
      }
      if (!leasedRateValid) {
        row.leasedRate = undefined
      }
      return row
    })
}

export type LeaseCompsStatsData = {
  averageLeasedRate: number
  averageListedRate: number
  averageAvailableSf: number
  medianLeaseRate: number
  averageAcres: number
  averageLotSizeSf: number
}

export function getLeaseCompsStatsData(
  transactions: (ListingTransaction & { listing: Listing })[],
  isLandReport: boolean
): LeaseCompsStatsData {
  const leasedRatesList: number[] = []
  const listedRatesList: number[] = []
  const availableSfList: number[] = []
  const acresList: number[] = []
  const lotSizeSfList: number[] = []

  const data = getLeaseTransactionsData(transactions, isLandReport, true)
  data.forEach((data) => {
    if (isLandReport && !data.acres) return
    if (
      data.leasedRate &&
      isValidLeaseRate(data.leasedRate, [data.propertyType])
    )
      leasedRatesList.push(data.leasedRate)
    if (
      data.listedRate &&
      isValidLeaseRate(data.listedRate, [data.propertyType])
    )
      listedRatesList.push(data.listedRate)
    if (data.availableSf) availableSfList.push(data.availableSf)
    if (data.acres) acresList.push(data.acres)
    if (data.lotSizeSf) lotSizeSfList.push(data.lotSizeSf)
  })

  const averageLeasedRate =
    leasedRatesList.length > 0
      ? leasedRatesList.reduce((a, b) => a + b) / leasedRatesList.length
      : 0

  const averageListedRate =
    listedRatesList.length > 0
      ? listedRatesList.reduce((a, b) => a + b) / listedRatesList.length
      : 0

  const averageAvailableSf =
    availableSfList.length > 0
      ? availableSfList.reduce((a, b) => a + b) / availableSfList.length
      : 0
  const averageAcres =
    acresList.length > 0
      ? acresList.reduce((a, b) => a + b) / acresList.length
      : 0
  const averageLotSizeSf =
    lotSizeSfList.length > 0
      ? lotSizeSfList.reduce((a, b) => a + b) / lotSizeSfList.length
      : 0

  // Calculate median lease rate
  const sortedRates = [...leasedRatesList].sort((a, b) => a - b)
  const medianLeaseRate =
    sortedRates.length > 0
      ? sortedRates.length % 2 === 0
        ? (sortedRates[sortedRates.length / 2 - 1] +
            sortedRates[sortedRates.length / 2]) /
          2
        : sortedRates[Math.floor(sortedRates.length / 2)]
      : 0

  return {
    averageLeasedRate,
    averageListedRate,
    averageAvailableSf,
    medianLeaseRate,
    averageAcres,
    averageLotSizeSf,
  }
}

export type SaleListingRow = {
  _id: string
  address: string | React.ReactNode
  date: string
  confidential?: boolean
  propertyType: string
  salePrice?: number
  buildingSf?: number
  acres?: number
  lotSizeSf?: number
  salePricePerSf?: number
  salePricePerAcre?: number
}

export function getSaleListingsData(
  listings: Listing[],
  isLandReport: boolean
): SaleListingRow[] {
  // Filter for sale and saleLease listings
  const saleListings = listings.filter(
    (listing) => listing.type === 'sale' || listing.type === 'saleLease'
  )

  const data = saleListings.map((listing, index) => {
    const salePrice = getNormalizedSalePrice(listing)
    const buildingSf = listing.meta.buildingSf
    const acres = listing.meta.acres

    const row: SaleListingRow = {
      _id: listing._id,
      address: (
        <Link
          style={tw('text-black')}
          href={`https://${listing.organization.slug}.resimplifi.com/listings/${listing.slug}`}>
          {`${index + 1}. ${listing.property.address || listing.name}`}
        </Link>
      ),
      date: new Date(listing.activeDate || listing.created)
        .toISOString()
        .split('T')[0],
      confidential: false, // Listings don't have confidential flag like transactions
      propertyType: listing.propertyTypes[0] || '',
      salePrice,
      buildingSf,
      acres,
    }

    if (isLandReport && acres) {
      row.lotSizeSf = Math.round(acres * 43560)
      if (salePrice) {
        row.salePricePerAcre = Math.round(salePrice / acres)
        row.salePricePerSf =
          Math.round((salePrice / (acres * 43560)) * 100) / 100
      }
    } else {
      // Handle 1sf building size or any under reasonable threshold
      if (salePrice && buildingSf && buildingSf > 100) {
        row.salePricePerSf = Math.round((salePrice / buildingSf) * 100) / 100
      }
    }

    return row
  })

  return data
}

export type SaleListingsStatsData = {
  averageSalePrice: number
  averageSalePricePerSf: number
  averageSalePricePerAcre: number
  averageBuildingSf: number
  averageAcres: number
  averageAcresSf: number
}

export function getSaleListingsStatsData(
  saleData: SaleListingRow[]
): SaleListingsStatsData {
  const salePricesList: number[] = []
  const buildingSfList: number[] = []
  const acresList: number[] = []
  const lotSizeSf: number[] = []
  const salePricePerSfList: number[] = []
  const salePricePerAcreList: number[] = []

  saleData.forEach((data) => {
    // Only include in stats if not an outlier
    const isValidPrice =
      !data.salePrice || isValidSalePrice(data.salePrice, [data.propertyType])

    if (isValidPrice) {
      if (data.salePrice) salePricesList.push(data.salePrice)

      if (data.buildingSf) buildingSfList.push(data.buildingSf)
      if (data.salePricePerSf) salePricePerSfList.push(data.salePricePerSf)

      if (data.acres) acresList.push(data.acres)
      if (data.lotSizeSf) lotSizeSf.push(data.lotSizeSf)
      if (data.salePricePerAcre)
        salePricePerAcreList.push(data.salePricePerAcre)
    }
  })

  const averageSalePrice =
    salePricesList.length > 0
      ? salePricesList.reduce((a, b) => a + b) / salePricesList.length
      : 0
  const averageSalePricePerSf =
    salePricePerSfList.length > 0
      ? salePricePerSfList.reduce((a, b) => a + b) / salePricePerSfList.length
      : 0
  const averageSalePricePerAcre =
    salePricePerAcreList.length > 0
      ? salePricePerAcreList.reduce((a, b) => a + b) /
        salePricePerAcreList.length
      : 0
  const averageBuildingSf =
    buildingSfList.length > 0
      ? buildingSfList.reduce((a, b) => a + b) / buildingSfList.length
      : 0
  const averageAcres =
    acresList.length > 0
      ? acresList.reduce((a, b) => a + b) / acresList.length
      : 0
  const averageAcresSf =
    lotSizeSf.length > 0
      ? lotSizeSf.reduce((a, b) => a + b) / lotSizeSf.length
      : 0

  return {
    averageSalePrice,
    averageSalePricePerSf,
    averageSalePricePerAcre,
    averageBuildingSf,
    averageAcres,
    averageAcresSf,
  }
}

// Lease Space Comp utilities
export type LeaseSpaceRow = {
  _id: string
  address: string | React.ReactNode
  spaceName?: string
  minRate?: number
  maxRate?: number
  averageRate?: number
  minAvailableSf?: number
  maxAvailableSf?: number
  acres?: number
  lotSizeSf?: number
  date: string
  propertyType: string
}

export function getLeaseSpacesData(
  listings: Listing[],
  isLandReport: boolean,
  filters?: MapFiltersType
): LeaseSpaceRow[] {
  // Filter for lease and saleLease listings, then extract lease spaces
  const leaseSpaces: Array<{ space: ListingLeaseSpace; listing: Listing }> = []

  listings
    .filter(
      (listing) => listing.type === 'lease' || listing.type === 'saleLease'
    )
    .forEach((listing) => {
      const spaces = (listing.leaseSpaces ?? []).filter(
        (space) => space.available
      )
      const { matches } = getFilteredLeaseSpaces(
        filters,
        spaces,
        listing.propertyTypes[0]
      )
      matches.forEach((space) => {
        leaseSpaces.push({ space, listing })
      })
    })

  leaseSpaces.sort((a, b) => {
    const dateA = new Date(a.space.created).getTime()
    const dateB = new Date(b.space.created).getTime()
    return dateB - dateA
  })

  const data = leaseSpaces.map(({ space, listing }, index) => {
    const minRate = space.minRate
      ? getNormalizedRate(
          space.minRate,
          space.rateType,
          space.minAvailableSf,
          isLandReport ? space.acres : undefined
        )
      : undefined
    const maxRate = space.maxRate
      ? getNormalizedRate(
          space.maxRate,
          space.rateType,
          space.maxAvailableSf,
          isLandReport ? space.acres : undefined
        )
      : minRate

    let averageRate
    const validatedLeaseRateMin =
      minRate && isValidLeaseRate(minRate, listing.propertyTypes)
        ? minRate
        : undefined
    const validatedLeaseRateMax =
      maxRate && isValidLeaseRate(maxRate, listing.propertyTypes)
        ? maxRate
        : undefined
    if (validatedLeaseRateMin && validatedLeaseRateMax) {
      averageRate = (validatedLeaseRateMin + validatedLeaseRateMax) / 2
    } else {
      averageRate = validatedLeaseRateMin || validatedLeaseRateMax
    }
    // Calculate DOM: lease space created date to today
    const date = new Date(space.created).toISOString().split('T')[0]

    const row: LeaseSpaceRow = {
      _id: space._id,
      address: (
        <Link
          style={tw('text-black')}
          href={`https://${listing.organization.slug}.resimplifi.com/listings/${listing.slug}`}>
          {`${index + 1}. ${listing.property.address || listing.name}`}
        </Link>
      ),
      spaceName: space.name,
      minRate,
      maxRate,
      averageRate,
      minAvailableSf: space.minAvailableSf,
      maxAvailableSf: space.maxAvailableSf,
      acres: space.acres,
      lotSizeSf: space.acres ? Math.round(space.acres * 43560) : undefined,
      date,
      propertyType: listing.propertyTypes[0] || '',
    }
    return row
  })

  return data
}

export type LeaseSpacesStatsData = {
  averageRate: number
  averageAvailableSf: number
  averageAcres: number
  totalSpaces: number
}

export function getLeaseSpacesStatsData(
  listings: Listing[],
  isLandReport: boolean,
  filters: MapFiltersType | undefined
): LeaseSpacesStatsData {
  const ratesList: number[] = []
  const availableSfList: number[] = []
  const acresList: number[] = []

  const spacesData = getLeaseSpacesData(listings, isLandReport, filters)
  spacesData.forEach((data) => {
    // Only include in stats if not an outlier
    const propertyTypes = [data.propertyType]
    const isValidMinRate =
      !data.minRate || isValidLeaseRate(data.minRate, propertyTypes)
    const isValidMaxRate =
      !data.maxRate || isValidLeaseRate(data.maxRate, propertyTypes)

    if (isValidMinRate && isValidMaxRate) {
      const rate =
        data.minRate && data.maxRate
          ? (data.minRate + data.maxRate) / 2
          : data.minRate || data.maxRate
      if (rate) {
        ratesList.push(rate)
        const availableSf =
          data.minAvailableSf && data.maxAvailableSf
            ? (data.minAvailableSf + data.maxAvailableSf) / 2
            : data.minAvailableSf || data.maxAvailableSf
        if (availableSf) availableSfList.push(availableSf)
        if (data.acres) acresList.push(data.acres)
      }
    }
  })

  const averageRate =
    ratesList.length > 0
      ? ratesList.reduce((a, b) => a + b) / ratesList.length
      : 0
  const averageAvailableSf =
    availableSfList.length > 0
      ? availableSfList.reduce((a, b) => a + b) / availableSfList.length
      : 0
  const averageAcres =
    acresList.length > 0
      ? acresList.reduce((a, b) => a + b) / acresList.length
      : 0

  const totalSpaces = spacesData.length

  return {
    averageRate,
    averageAvailableSf,
    averageAcres,
    totalSpaces,
  }
}
